import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import planoContratualService from "services/PlanoContratualService";
import usuarioService from "services/UsuarioService";
import FlipEscolhaBloqueada from "views/app-views/conta-bloqueada/FlipEscolhaBloqueada";

/**
 * Aviso PROATIVO do flip (motor v2). Quando o profissional passa do limite de alunos,
 * o back marca `flip_pendente_em` (prazo de FLIP_JANELA_DIAS = 7 dias); se ele não
 * escolher um plano até lá, a conta é BLOQUEADA (só libera escolhendo+pagando, na
 * página /conta-bloqueada).
 *
 * Antes desta tela o único aviso proativo era um Alert dentro de /assinatura — que
 * exige VISITAR a página. Quem não abria /assinatura só via a escolha quando a conta
 * JÁ estava bloqueada. Este modal aparece em QUALQUER página durante TODA a janela do
 * flip (1x por sessão), com contagem regressiva, e deixa escolher+pagar na hora
 * (reusa FlipEscolhaBloqueada). No bloqueio (prazo vencido → flip_pendente_em no
 * passado) este modal NÃO dispara — o caminho passa a ser a /conta-bloqueada.
 *
 * Segue o padrão dos outros modais globais do app-layout (CortesiaFimModal etc.):
 * autocontido, decide sozinho se aparece, fetch de background silencioso.
 */
const chaveSessao = (flipPendenteEm) =>
	`flip_pendente_modal_dispensado_${flipPendenteEm}`;

const FlipPendenteModal = () => {
	const [visible, setVisible] = useState(false);
	const [planoAtual, setPlanoAtual] = useState(null);
	const [userData, setUserData] = useState(null);
	const [taxas, setTaxas] = useState([]);

	useEffect(() => {
		let ativo = true;
		(async () => {
			try {
				const plano = await planoContratualService.getPlanoAtual();
				if (!ativo || !plano) return;

				const motorV2 = plano.motor === "v2";
				const flipEm = plano.flip_pendente_em
					? new Date(plano.flip_pendente_em)
					: null;
				const opcoes = Array.isArray(plano.flip_opcoes)
					? plano.flip_opcoes
					: [];
				// Só na JANELA (flip_pendente_em no futuro) e com opções pra escolher.
				// Vencido = bloqueado → é a /conta-bloqueada, não este modal.
				const dentroDaJanela = flipEm && flipEm.getTime() > Date.now();
				if (!(motorV2 && dentroDaJanela && opcoes.length > 0)) return;

				// 1x por sessão POR janela de flip (a chave inclui o flip_pendente_em,
				// então uma janela nova — outro flip — volta a aparecer).
				try {
					if (
						sessionStorage.getItem(
							chaveSessao(plano.flip_pendente_em),
						)
					) {
						return;
					}
				} catch (_e) {
					/* sessionStorage indisponível: segue mostrando */
				}

				const [dados, taxasRes] = await Promise.all([
					usuarioService.obterPropriosDados().catch(() => null),
					planoContratualService
						.getTaxasParcelamentoCartao()
						.catch(() => []),
				]);
				if (!ativo) return;
				setPlanoAtual(plano);
				setUserData(dados);
				setTaxas(taxasRes || []);
				setVisible(true);
			} catch (_e) {
				/* silencioso: é um check de background, não trava a app */
			}
		})();
		return () => {
			ativo = false;
		};
	}, []);

	const diasRestantes = planoAtual?.flip_pendente_em
		? Math.max(
				0,
				Math.ceil(
					(new Date(planoAtual.flip_pendente_em).getTime() -
						Date.now()) /
						86400000,
				),
		  )
		: 0;

	const dispensar = () => {
		try {
			sessionStorage.setItem(
				chaveSessao(planoAtual?.flip_pendente_em),
				"1",
			);
		} catch (_e) {
			/* ignore */
		}
		setVisible(false);
	};

	// Pagou/agendou: o flip se resolve no back; fecha o modal (a próxima sessão
	// não reabre porque flip_pendente_em deixa de vir).
	const aoPagar = () => setVisible(false);

	if (!planoAtual) return null;

	return (
		<Modal
			open={visible}
			footer={null}
			width={760}
			centered
			closable
			maskClosable={false}
			onCancel={dispensar}
			bodyStyle={{ paddingTop: 16 }}
		>
			<FlipEscolhaBloqueada
				planoAtual={planoAtual}
				userData={userData}
				taxasParcelamentoCartao={taxas}
				diasRestantes={diasRestantes}
				onPago={aoPagar}
			/>
			<div style={{ textAlign: "center", marginTop: 8 }}>
				<Button type="text" onClick={dispensar}>
					Deixar para depois
				</Button>
			</div>
		</Modal>
	);
};

export default FlipPendenteModal;
